// @ts-nocheck
import { Link } from "@tanstack/react-router";
import { Scissors, Layers, Package, ArrowUpRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";

interface PaperSupplyTabProps {
  isLoading: boolean;
  paperStats: {
    totalMaterials: number;
    totalSlitted: number;
    pendingSlit: number;
    longGrainCount: number;
    shortGrainCount: number;
    totalPallets: number;
    totalStockPcs: number;
    topSurplusItems: Array<{
      id: string;
      poId: string;
      poCode: string;
      productName: string;
      stockPcs: number;
      printingPaperSize?: string;
      specification?: string;
      grainDirection?: string;
    }>;
  };
  earlyWarnings?: any;
  onDrillDown?: (config: { type: string; title: string; status?: string }) => void;
}

export function PaperSupplyTab({
  isLoading,
  paperStats,
  earlyWarnings,
  onDrillDown,
}: PaperSupplyTabProps) {
  const {
    totalMaterials = 0,
    totalSlitted = 0,
    pendingSlit = 0,
    longGrainCount = 0,
    shortGrainCount = 0,
    totalPallets = 0,
    totalStockPcs = 0,
    topSurplusItems = [],
  } = paperStats || {};

  const zIndex = useZIndexView();

  const handleRowClick = (poId?: string) => {
    if (!poId) return;
    if (window.getSelection()?.toString().trim().length) return;
    zIndex.openView("productionOrder", poId);
  };

  const slitRate = totalMaterials > 0 ? Math.round((totalSlitted / totalMaterials) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Thanh cảnh báo vận hành cấp phát giấy */}
      <div className="flex flex-wrap items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs">
        <span className="font-bold text-amber-700 flex items-center gap-1.5 shrink-0">
          <Scissors className="h-3.5 w-3.5" />
          Cảnh báo giấy in:
        </span>
        <div className="flex flex-wrap items-center gap-2 text-foreground">
          <Badge
            variant="outline"
            onClick={() =>
              onDrillDown?.({
                type: "paper_surplus",
                title: "Phôi in dôi dư lưu kho",
              })
            }
            className="bg-card border-amber-300 text-amber-800 font-semibold cursor-pointer hover:bg-amber-100 transition-colors"
          >
            {`📦 ${totalStockPcs.toLocaleString()} tờ giấy dôi dư (stockPcs)`}
          </Badge>
          <Badge
            variant="outline"
            className="bg-card border-border text-foreground font-semibold"
          >
            ✂️ {pendingSlit} lệnh chờ xả mộc ({slitRate}% hoàn thành)
          </Badge>
          <Badge
            variant="outline"
            className="bg-card border-emerald-200 text-emerald-700 font-semibold"
          >
            {`🏷️ ${totalPallets.toLocaleString()} kiện pallet đã phát hành`}
          </Badge>
        </div>
      </div>

      {/* 4 Thẻ KPI Tối Giản */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-border">
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="border-border border-t-4 border-t-blue-500 bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wide">
                    Đã xả phôi
                  </span>
                  <Badge variant="outline" className="text-blue-700 bg-blue-500/10 border-blue-500/20 text-[10px] font-bold">
                    {slitRate}%
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-2xl font-black text-foreground font-mono">
                    {totalSlitted}
                    <span className="text-sm font-normal text-muted-foreground ml-1">/ {totalMaterials} lệnh</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border border-t-4 border-t-emerald-500 bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wide">
                    Chiều xớ giấy
                  </span>
                  <Badge variant="outline" className="text-emerald-700 bg-emerald-500/10 border-emerald-500/20 text-[10px] font-bold">
                    Khổ in Offset
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-sm font-bold text-foreground">
                    <span className="text-xl font-black font-mono text-emerald-600 mr-1">{longGrainCount}</span> xớ xuôi
                    <span className="mx-2 text-muted-foreground">·</span>
                    <span className="text-xl font-black font-mono text-amber-600 mr-1">{shortGrainCount}</span> xớ ngang
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border border-t-4 border-t-purple-500 bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wide">
                    Kiện Pallet
                  </span>
                  <Badge variant="outline" className="text-purple-700 bg-purple-500/10 border-purple-500/20 text-[10px] font-bold">
                    Đã phát hành
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-2xl font-black text-foreground font-mono">
                    {totalPallets.toLocaleString()}
                    <span className="text-sm font-normal text-muted-foreground ml-1">kiện</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card
              className="border-border border-t-4 border-t-amber-500 bg-card cursor-pointer hover:border-amber-400 transition-colors"
              onClick={() =>
                onDrillDown?.({
                  type: "paper_surplus",
                  title: "Phôi in dôi dư lưu kho",
                })
              }
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wide">
                    Giấy in dôi dư
                  </span>
                  <Badge variant="outline" className="text-amber-700 bg-amber-500/10 border-amber-500/20 text-[10px] font-bold">
                    Lưu kho
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-2xl font-black text-amber-600 font-mono">
                    {totalStockPcs.toLocaleString()}
                    <span className="text-sm font-normal text-muted-foreground ml-1">tờ</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Danh sách bài in dôi dư lưu kho (Tối giản - Không subtitle) */}
      <Card className="shadow-xs border border-border">
        <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-amber-500" /> Phôi in dôi dư lưu kho
          </CardTitle>
          <Link to="/planning/paper-allocation">
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary">
              Xem Hub Cấp Phát
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : topSurplusItems.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              Không có phôi in dôi dư lưu kho
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-muted/50 border-y border-border text-muted-foreground uppercase text-[10px]">
                  <tr>
                    <th className="py-2 px-3 font-semibold">Mã Lệnh (PO)</th>
                    <th className="py-2 px-3 font-semibold">Sản phẩm</th>
                    <th className="py-2 px-3 font-semibold">Khổ in & Quy cách</th>
                    <th className="py-2 px-3 font-semibold text-center">Xớ giấy</th>
                    <th className="py-2 px-3 font-semibold text-right">Dôi dư</th>
                    <th className="py-2 px-3 font-semibold text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {topSurplusItems.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => handleRowClick(item.poId)}
                      className="hover:bg-indigo-50/60 cursor-pointer transition-colors"
                    >
                      <td className="py-2 px-3 font-mono font-medium text-primary">
                        {item.poCode}
                      </td>
                      <td className="py-2 px-3 font-medium text-foreground max-w-[220px] truncate">
                        {item.productName}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {item.printingPaperSize || item.specification || "—"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Badge
                          variant="outline"
                          className={
                            item.grainDirection === "LONG_GRAIN"
                              ? "border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]"
                              : "border-amber-300 text-amber-700 bg-amber-50 text-[10px]"
                          }
                        >
                          {item.grainDirection === "LONG_GRAIN" ? "Xớ xuôi" : "Xớ ngang"}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 font-mono font-bold text-amber-600 text-right">
                        {item.stockPcs.toLocaleString()} tờ
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Link
                          to="/planning/paper-allocation"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="outline" size="sm" className="h-6 text-[11px] px-2">
                            Thu hồi
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ZIndexView
        open={zIndex.open}
        onOpenChange={zIndex.onOpenChange}
        type={zIndex.type}
        id={zIndex.id}
      />
    </div>
  );
}
